<template>
  <div class="filter-path">
    <div :style="{ textAlign: align }" class="filter-card">
      <div class="left">
        <c-input
          v-if="searchable"
          v-model:value="txtSearch"
          :placeholder="placeholder"
          :label="label"
          :schema="updateSearch"
          @enter="onsubmit(isSearchEnter)"
          searchable
          :left="false"
          type="text"
          className="filter-search-input"
        ></c-input>
      </div>
      <div class="right">
        <div
          v-outside="outsideClick"
          @keydown="handleKeyDown"
          @keydown.stop
          :class="isToggle ? 'show' : 'hide'"
        >
          <div class="row flex">
            <div class="row">
              <div class="filter-card-item">
                <button
                  v-if="body && body.length"
                  @click="isToggle = !isToggle"
                  class="btn btn-white btn-filter"
                  style="padding: var(--input-padding)"
                >
                  <img :src="filterIcon" alt="" />
                  <span>{{ $t("componentFilter") }}</span>
                  <img
                    :src="isToggle ? filterIconCaretUp : filterIconCaret"
                    alt=""
                  />
                </button>
              </div>
              <div class="filter-dropdown" :style="`min-width: ${width}`">
                <div class="filter-header">
                  <div class="header-left">{{ $t("componentFilter") }}</div>
                  <div class="header-right"></div>
                </div>
                <div class="filter-content">
                  <div
                    v-for="(item, index) in body"
                    :key="index"
                    :class="item.class"
                  >
                    <component
                      :key="item"
                      :is="item.component"
                      :label="item.label"
                      v-model:value="item.value"
                      :schema="item.schema || schema"
                      :type="item.type"
                      :class="item.class"
                      :options="item.options"
                      :multiple="item.multiple"
                      :required="item.required"
                      :disabled="item.disabled"
                      :searchable="item.searchable"
                      :optionValue="item.optionValue"
                      :optionLabel="item.optionLabel"
                      :align="item.align"
                      @enter="item.enter || submit"
                      :clear="item.clear"
                    />
                  </div>
                </div>

                <div class="filter-footer">
                  <button
                    v-if="exitLabel"
                    @click="onreset"
                    class="btn btn-gray btn-filter"
                  >
                    {{ $t(exitLabel) }}
                  </button>
                  <button
                    v-if="submitLabel"
                    @click="onsubmit(true)"
                    class="btn btn-primary btn-filter"
                  >
                    {{ $t(submitLabel) }}
                  </button>
                </div>
              </div>
            </div>
            <div class="row flex">
              <slot name="search"></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="filter-result">
      <div class="left">
        <span class="font20 bold"
          ><span class="txt-primary">{{
            formatNumber(meta?.length || 0)
          }}</span>
          {{
            meta?.length > 1
              ? $t("componentSearchResults")
              : $t("componentSearchResult")
          }}</span
        >
      </div>
      <div v-if="body && body.length" class="right filter-result-right">
        <span
          v-show="item.value"
          v-for="item in body"
          :key="item.key"
          class="ml10 flex"
        >
          <span class="fix-filter-input"
            >{{
              item?.showLabel ? (item?.label ? $t(item?.label) + ":" : "") : ""
            }}
            {{ formatValue(item) }}
            <i
              v-if="item.delete"
              @click="handleDelete(item)"
              @click.stop
              @dblclick.stop
              class="fa fa-times-circle bold ic-arrow-down ml5 pointer"
              style="right: 40px; color: var(--gray-dark)"
            ></i
          ></span>
        </span>

        <span
          @click="onreset"
          class="clear-all-btn txt-detail sticky-scrollbar"
          >{{ $t("componentClearAll") }}</span
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { watch } from "vue";
import { defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import { formatDate } from "@/services/utils/helper.util";

interface BodyItem {
  label: string;
  value: string;
  key: string;
  type?: string;
  component: "c-input" | "c-select" | "c-multi-check";
  class: "col-6" | "col-12" | "fix-filter";
  options?: string[];
  multiple?: boolean;
  required?: boolean;
  disabled?: boolean;
  searchable?: boolean;
  optionValue?: string;
  optionLabel?: string;
  clear?: boolean;
  delete?: boolean;
  showLabel?: boolean;
  align?: "left" | "center" | "right";
  format?: () => void;
  schema?: () => void;
  enter?: () => void;
}

export default defineComponent({
  name: "c-list-search",

  props: {
    format: { type: Function as unknown as () => () => void },
    manual: { type: Function as unknown as () => () => void },
    schema: { type: Function as unknown as () => () => void },
    submit: { type: Function as unknown as () => () => void },
    exit: { type: Function as unknown as () => () => void },
    deleteItem: { type: Function as unknown as () => () => void },
    body: { type: Array as () => BodyItem[], default: () => [] },
    meta: { type: Object, default: () => ({}) },
    search: { type: String, default: "" },
    searchable: { type: Boolean, default: true },
    isSearchEnter: { type: Boolean, default: true },
    label: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    width: { type: String, default: "500px" },
    align: {
      type: String as () => "left" | "center" | "right",
      default: "right",
      validator: (value: string) => ["left", "center", "right"].includes(value),
    },
    exitLabel: {
      type: String as () => "componentReset",
      default: "componentReset",
    },
    submitLabel: {
      type: String as () => "componentApply",
      default: "componentApply",
    },
  },

  emits: ["update:search", "onSearch"],

  setup(props, { emit }) {
    const txtSearch = ref(props.search);
    const { t } = useI18n();

    watch(
      () => props.search,
      (newVal) => {
        txtSearch.value = newVal;
      }
    );

    function updateSearch() {
      emit("update:search", txtSearch.value);

      emit("onSearch");
      props.schema;
      props.manual;
    }

    const isToggle = ref(false);

    function outsideClick() {
      isToggle.value = false;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        isToggle.value = false;
      }
    };

    const onreset = () => {
      if (typeof props.exit === "function") {
        props.exit();
      }
      isToggle.value = false;
    };

    const onsubmit = (isAbleSearch: boolean) => {
      if (isAbleSearch && typeof props.submit === "function") {
        props.submit();
      }
      isToggle.value = false;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleDelete = (item: any) => {
      onsubmit(true);
      item.value = "";
    };

    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const filterIcon = require("@/assets/icons/filter.svg");
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const filterIconCaret = require("@/assets/icons/caret-down.svg");
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const filterIconCaretUp = require("@/assets/icons/caret-up.svg");

    function formatNumber(number: number) {
      return `${number || 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function formatValue(item: any): string | undefined {
      if (item?.type === "date" && item?.value) {
        return formatDate(item.value);
      }

      if (Array.isArray(item?.value)) {
        return item.value.length + " " + t("componentItemSelected");
      }

      if (item?.options) {
        const option = item.options.find(
          // eslint-disable-nextline @typescript-eslint/no-explicit-any
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (opt: any) => String(opt[item.optionValue]) === String(item.value)
        );

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return option?.[item.optionLabel as keyof any];
      }

      return item.value as string | undefined;
    }

    return {
      txtSearch,
      updateSearch,
      isToggle,
      t,
      outsideClick,
      handleKeyDown,
      onsubmit,
      onreset,
      filterIcon,
      filterIconCaret,
      filterIconCaretUp,
      formatNumber,
      formatValue,
      handleDelete,
    };
  },
});
</script>
<style lang="scss" src="./style.scss"></style>
